import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaExclamationTriangle, FaChevronDown, FaChevronUp, FaClock } from "react-icons/fa";
import { fetchUnrespondedTickets } from "../../api/overviewApi";
import ChatDrawer from "./ChatDrawer";

// ── Reusable ticket row ───────────────────────────────────────────────────────
const TicketRow = ({ t, isDark, dayColor, setChatTicket }) => {
  const [isBtnHovered, setIsBtnHovered]         = useState(false);
  const [isToggleHovered, setIsToggleHovered]   = useState(false);
  const [expanded, setExpanded]                 = useState(false);

  const formattedDate = t.create_date
    ? t.create_date.replace("T", " ").slice(0, 19)
    : null;

  const rowAccent  = "#dc2626";
  const mutedColor = isDark ? "rgba(255,255,255,0.28)" : "#a0a0b8";
  const hasDetails = !!(t.ticket_description || t.latest_comment?.text || t.latest_comment?.title);

  const formatNoteDate = iso => iso ? iso.replace("T", " ").slice(0, 16) : "";

  return (
    <div
      style={{
        borderRadius: 6,
        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#eeeef5"}`,
        borderLeft: `4px solid ${rowAccent}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {/* ── Main flex row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>

        {/* Days badge */}
        <span style={{
          flexShrink: 0, minWidth: 40, textAlign: "center",
          background: `rgba(${t.days_unresponded >= 14 ? "220,38,38" : "245,158,11"},0.1)`,
          border: `1px solid rgba(${t.days_unresponded >= 14 ? "220,38,38" : "245,158,11"},0.3)`,
          borderRadius: 5, padding: "2px 5px",
          fontSize: 10, fontWeight: 700, color: dayColor(t.days_unresponded),
        }}>
          {t.days_unresponded}d
        </span>

        {/* Content block */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Row 1: status badge + ticket number */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em",
              color: "#dc2626",
              background: isDark ? "rgba(220,38,38,0.15)" : "rgba(220,38,38,0.08)",
              border: "1px solid rgba(220,38,38,0.3)",
              borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap",
            }}>
              Unresponded
            </span>
            <span style={{ fontSize: 10.5, color: isDark ? "rgba(255,255,255,0.3)" : "#b8b8cc", fontFamily: "monospace" }}>
              {t.ticket_number || `#${t.id}`}
            </span>
          </div>

          {/* Row 2: title */}
          <div style={{
            fontSize: 13, fontWeight: 600, lineHeight: 1.35,
            color: isDark ? "#f0f0f8" : "#18182e",
            marginBottom: 4,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {t.title}
          </div>

          {/* Row 3: timestamp + View Details toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {formattedDate ? (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: mutedColor }}>
                <FaClock size={9.5} />
                {formattedDate}
              </div>
            ) : <span />}

            {hasDetails && (
              <button
                onMouseEnter={() => setIsToggleHovered(true)}
                onMouseLeave={() => setIsToggleHovered(false)}
                onClick={() => setExpanded(e => !e)}
                style={{
                  display: "flex", alignItems: "center", gap: 3,
                  background: "none", border: "none", padding: "0 2px",
                  fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                  color: isToggleHovered ? "#6366f1" : mutedColor,
                  transition: "color 0.15s",
                }}
              >
                {expanded ? <FaChevronUp size={8} /> : <FaChevronDown size={8} />}
                {expanded ? "Hide" : "Details"}
              </button>
            )}
          </div>
        </div>

        {/* Ask AI button */}
        <button
          onMouseEnter={() => setIsBtnHovered(true)}
          onMouseLeave={() => setIsBtnHovered(false)}
          onClick={e => { e.stopPropagation(); setChatTicket(t); }}
          style={{
            flexShrink: 0, display: "flex", alignItems: "center", gap: 6,
            padding: "7px 14px", borderRadius: 20,
            background: isBtnHovered ? "#6366f1" : (isDark ? "rgba(255,255,255,0.07)" : "#f0f0f8"),
            border: "none",
            color: isBtnHovered ? "#ffffff" : (isDark ? "rgba(255,255,255,0.5)" : "#7070a0"),
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            transition: "background 0.15s ease, color 0.15s ease",
            whiteSpace: "nowrap",
            boxShadow: isBtnHovered ? "0 4px 12px rgba(99,102,241,0.4)" : "none",
          }}
        >
          ✨ Ask AI
        </button>
      </div>

      {/* ── Expandable details panel ── */}
      <AnimatePresence>
        {expanded && hasDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              margin: "0 12px 10px",
              padding: "10px 12px",
              borderRadius: 6,
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
              display: "flex", flexDirection: "column", gap: 10,
            }}>

              {/* Description */}
              {t.ticket_description && (
                <div>
                  <div style={{
                    fontSize: 9.5, fontWeight: 700, letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: isDark ? "rgba(255,255,255,0.28)" : "#9090b0",
                    marginBottom: 5,
                  }}>
                    Description
                  </div>
                  <p style={{
                    margin: 0, fontSize: 12, lineHeight: 1.55,
                    color: isDark ? "rgba(255,255,255,0.48)" : "#5a5a7a",
                    wordBreak: "break-word",
                  }}>
                    {t.ticket_description}
                  </p>
                </div>
              )}

              {/* Divider between sections */}
              {t.ticket_description && t.latest_comment?.text && (
                <div style={{ height: 1, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }} />
              )}

              {/* Latest note */}
              {t.latest_comment?.text && (
                <div>
                  <div style={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", flexWrap: "wrap",
                    gap: 4, marginBottom: 5,
                  }}>
                    <div style={{
                      fontSize: 9.5, fontWeight: 700, letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: isDark ? "rgba(255,255,255,0.28)" : "#9090b0",
                    }}>
                      Latest Note
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: mutedColor }}>
                      {t.latest_comment.author && <span>{t.latest_comment.author}</span>}
                      {t.latest_comment.date && (
                        <span style={{ color: isDark ? "rgba(255,255,255,0.2)" : "#c0c0d4" }}>
                          · {formatNoteDate(t.latest_comment.date)}
                        </span>
                      )}
                    </div>
                  </div>
                  {t.latest_comment.title && (
                    <div style={{
                      fontSize: 11.5, fontWeight: 600, marginBottom: 4,
                      color: isDark ? "rgba(255,255,255,0.55)" : "#4a4a6a",
                    }}>
                      {t.latest_comment.title}
                    </div>
                  )}
                  <p style={{
                    margin: 0, fontSize: 12, lineHeight: 1.55,
                    color: isDark ? "rgba(255,255,255,0.48)" : "#5a5a7a",
                    wordBreak: "break-word",
                  }}>
                    {t.latest_comment.text}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * UnrespondedAlert
 * Displays a compact alert for tickets unresponded for 2+ days.
 * Owns the ChatDrawer entirely — no event bus, no parent wiring needed.
 */
const UnrespondedAlert = ({ clientId, isDark, onReviewClick, onCountChange }) => {
  const [tickets, setTickets]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(false);
  const [chatTicket, setChatTicket] = useState(null); // null = drawer closed

  useEffect(() => {
    if (!clientId) {
      setTickets([]);
      if (onCountChange) onCountChange(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchUnrespondedTickets(clientId, 2)
      .then(res => {
        const t = res.tickets || [];
        setTickets(t);
        if (onCountChange) onCountChange(t.length);
      })
      .catch(() => { setTickets([]); if (onCountChange) onCountChange(0); })
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading || tickets.length === 0) return null;

  const dayColor = d => d >= 14 ? "#dc2626" : "#f59e0b";

  return (
    <>
      {/* ── Alert card ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25 }}
        style={{
          background: "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(245,158,11,0.05))",
          border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 14,
        }}
      >
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>

          {/* Icon + text */}
          <div style={{ display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: "rgba(239,68,68,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <FaExclamationTriangle size={12} color="#dc2626" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "#dc2626", lineHeight: 1.3 }}>
                {tickets.length} ticket{tickets.length !== 1 ? "s" : ""} unresponded for 2+ days
              </div>
              <div style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.35)" : "#999", marginTop: 1 }}>
                Requires immediate attention
              </div>
            </div>
          </div>

          {/* Unified expand / collapse button */}
          <button
            onClick={() => { setExpanded(e => !e); if (onReviewClick) onReviewClick(); }}
            style={{
              flexShrink: 0,
              display: "flex", alignItems: "center", gap: 6,
              background: "#dc2626", color: "#fff", border: "none",
              borderRadius: 6, padding: "6px 12px",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              transition: "background 0.15s", whiteSpace: "nowrap",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#b91c1c"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#dc2626"; }}
          >
            {expanded ? (
              <>Hide <FaChevronUp size={9} /></>
            ) : (
              <>Review All Tickets (+{tickets.length}) <FaChevronDown size={9} /></>
            )}
          </button>
        </div>

        {/* ── Ticket rows — first 2 always visible, rest behind expand ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
          {/* Always-visible: first 2 tickets */}
          {tickets.slice(0, 2).map((t, i) => (
            <TicketRow key={t.id || i} t={t} isDark={isDark} dayColor={dayColor} setChatTicket={setChatTicket} />
          ))}

          {/* Overflow rows — only rendered when expanded */}
          <AnimatePresence>
            {expanded && tickets.length > 2 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: "hidden", display: "flex", flexDirection: "column", gap: 6 }}
              >
                {tickets.slice(2).map((t, i) => (
                  <TicketRow key={t.id || i + 2} t={t} isDark={isDark} dayColor={dayColor} setChatTicket={setChatTicket} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* ── Chat Drawer (portalled at page level) ── */}
      <ChatDrawer
        ticket={chatTicket}
        onClose={() => setChatTicket(null)}
        isDark={isDark}
      />
    </>
  );
};

export default UnrespondedAlert;